import React, { useContext } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import Card from '@jarvis.oddle/core/components/Card'
import Div from '@jarvis.oddle/core/components/Div'
import Link from '@jarvis.oddle/core/components/Link'
import { H4 } from '@jarvis.oddle/core/components/Typo'
import {
  borderRadiusSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import UserContext from 'src/containers/UserContext'
import { NextLink } from 'src/components/molecules/ShopLink'

const Container = styled(Card).attrs(() => ({
  borderRadius: borderRadiusSizes.small
}))`
  z-index: 1;
  max-height: 60vh;
  overflow-y: auto;
  padding: ${padding[paddingSizes.medium]} ${padding[paddingSizes.none]};
`

const NavItem = styled(Div)`
  padding: ${padding[paddingSizes.small]} ${padding[paddingSizes.medium]};

  ${(props) =>
    props.selected && `box-shadow: inset 4px 0 0 0 ${props.theme.buttonColor};`}
  &:hover,
  &:focus {
    cursor: pointer;
    pointer-events: auto;
  }
`

const NavItemText = styled(H4)`
  ${(props) => props.selected && `color: ${props.theme.buttonColor};`}
  &:hover,
  &:focus {
    color: ${(props) => props.theme.buttonColor};
  }
`

const StyledLink = styled(Link)`
  text-decoration: none;
`

interface VerticalNavigationProps {
  items: Record<string, string>[]
  selectedItemId?: string
  onClickItem?: (x, y) => void
  syncScroll?: boolean
  className?: string
}

const VerticalNavigation = ({
  items,
  selectedItemId,
  onClickItem = (x) => x,
  syncScroll,
  className
}: VerticalNavigationProps): JSX.Element => {
  const { isEnabledCustomerSaveCreditCardOnStripe } = useContext(UserContext)
  const [prevSelectedItemId, setPrevSelectedItemId] =
    React.useState(selectedItemId)
  const refs = React.useMemo(() => new Map(), [])
  let containerRef = null

  const handleScroll = React.useCallback(() => {
    const container = ReactDOM.findDOMNode(containerRef) as HTMLElement
    const targetNode = ReactDOM.findDOMNode(
      refs.get(selectedItemId)
    ) as HTMLElement

    // Safety Check
    if (targetNode && container) {
      // Sanity Check
      if (
        container.scrollHeight > container.offsetHeight &&
        container.scrollTo
      ) {
        // position of the target Node is subtracted by half of the height of the container
        // So the previous and subsequent unselected items will remain visible
        const top =
          targetNode.offsetTop - Math.trunc(container.offsetHeight / 2)
        container.scrollTo({ top, behavior: 'smooth' })
      }
    }
  }, [containerRef, refs, selectedItemId])

  React.useEffect(() => {
    if (syncScroll && selectedItemId && prevSelectedItemId !== selectedItemId) {
      setPrevSelectedItemId(selectedItemId)
      handleScroll()
    }
  }, [selectedItemId, prevSelectedItemId, syncScroll, handleScroll])

  return (
    <Container
      className={className}
      ref={(e) => {
        containerRef = e
      }}
      id='product-section'
    >
      {(items || [])
        .filter((item) =>
          isEnabledCustomerSaveCreditCardOnStripe
            ? true
            : item.id !== 'paymentMethods'
        )
        .map((item) =>
          item.href ? (
            <NextLink key={item.id} href={item.href} as={item.as} passHref>
              <StyledLink>
                <NavItem
                  selected={item.id === selectedItemId}
                  onClick={(e) => onClickItem(e, item.id)}
                  ref={(e) => refs.set(item.id, e)}
                >
                  <NavItemText
                    bold
                    normalised
                    selected={item.id === selectedItemId}
                  >
                    {item.name}
                  </NavItemText>
                </NavItem>
              </StyledLink>
            </NextLink>
          ) : (
            <StyledLink key={item.id}>
              <NavItem
                selected={item.id === selectedItemId}
                onClick={(e) => onClickItem(e, item.id)}
                ref={(e) => refs.set(item.id, e)}
              >
                <NavItemText
                  bold
                  normalised
                  selected={item.id === selectedItemId}
                >
                  {item.name}
                </NavItemText>
              </NavItem>
            </StyledLink>
          )
        )}
    </Container>
  )
}

export default VerticalNavigation
